/**
 * Colour tiling — perfect colourings of the 17 wallpaper groups (brief §4).
 *
 * A "perfect colouring" with n colours assigns a colour to every placement of
 * the tile such that every isometry of the wallpaper group permutes the colour
 * classes consistently (Coxeter, "Coloured Symmetry"; Grünbaum & Shephard,
 * "Tilings and Patterns" ch. 8; Schattschneider's classification of Escher's
 * colour-symmetric drawings). This is NOT an arbitrary UI limit — most (n, group)
 * pairs simply have no consistent colouring, and offering them would produce a
 * pattern that visibly clashes at some seam.
 *
 * Derivation method (independent of the brief's rough sketch, which is only a
 * starting point per its own wording):
 *
 * Two distinct valid mechanisms produce a perfect colouring, and which one a
 * given (group, count) pair uses is a real design choice with a visible
 * consequence, not just an implementation detail:
 *
 * 1. **Point-group (rotation/reflection) colouring** — colour a placement by
 *    its op index alone: `colour = opIndex mod count`. Valid whenever `count`
 *    divides the point group's order N (standard modular fact: if K divides
 *    N, `(x mod N) mod K == x mod K` for any x, so composing with any group
 *    element g = T∘R_k permutes colours by a fixed shift depending only on k,
 *    regardless of translation). This is what an artist actually wants for a
 *    tile that IS one rotational/reflective slice of a larger symmetric unit
 *    (the "N-way" presets — Quarter turn, 3-way hex, 6-way triangle, …): each
 *    copy gets its own colour, uniformly, everywhere in the pattern.
 * 2. **Translation-sublattice colouring** — colour by which coset of an
 *    index-N sublattice of the translation lattice a placement's cell falls
 *    in. For the square lattice this is the ring of Gaussian integers Z[i]
 *    (rotation by 90° = multiply by i); for hexagonal it's the Eisenstein
 *    integers Z[ω]. Both are PIDs, so every point-group-invariant sublattice
 *    is a principal ideal (z), of index N = Norm(z) = z·z̄ — mirror-symmetric
 *    groups additionally need the ideal conjugation-invariant (z̄ an
 *    associate of z). This is the ONLY option when the wanted count doesn't
 *    divide the point group's order (nothing analogous to method 1 exists),
 *    and it colours by *cell*, not by rotational copy — visually a
 *    checkerboard/block pattern rather than "each copy a different colour."
 *
 * Method 1 is used below wherever the count divides the tile's point-group
 * order — p4/p4m's rotation/reflection counts, and the hex family's 3- and
 * 6-colourings (all divide 3, 6, 8 or 12 as appropriate). Method 2 remains
 * for the 7-colouring (7 doesn't divide 3 or 6 — no rotation-based analogue
 * exists) and for the 2-fold-only "checkerboard" groups (p1/p2/pg/pgg/pmg/
 * cm/cmm/pmm), which keep their glide-aware translation classifiers as
 * originally derived.
 *
 * Verified corrections against the brief's rough table:
 * - p3's point group (C3, order 3, prime) has no index-2 subgroup, so no
 *   6-colouring exists for p3 by either method. 6 is dropped for p3.
 * - The 7-colouring (norm-7 Eisenstein ideal, the classic Escher-lizard/
 *   Coxeter scheme) is generated by a non-self-conjugate integer (3+ω) —
 *   valid for the rotation-only groups p3/p6, but not the mirror groups
 *   p3m1/p31m/p6m (matches reality: Escher's lizard tiling is chiral and has
 *   no mirror line). 7 doesn't divide any of these groups' point-group
 *   orders either, so method 1 was never an option for it regardless.
 *
 * Deliberately NOT shipped despite being mathematically valid (scope
 * discipline — see final report): a chiral 5-colouring for p4 (norm-5 Gaussian
 * ideal, not self-conjugate so p4-only); a 4-colouring for all five hexagonal
 * groups (norm-4 ideal, self-conjugate, works everywhere but not requested).
 * 2-colourings for the translation/reflection-only groups (p1, p2, pm, cm,
 * pmm, cmm) are capped at 2 by product choice, not mathematical necessity —
 * with no rotation of order ≥3 there's no natural larger count that reads as
 * more than an arbitrary partition.
 *
 * Non-symmorphic (glide-containing) groups needed a genuinely different check:
 * for pg/pgg/pmg/p4g every non-identity point-group op carries an intrinsic
 * fractional lattice shift (there's no "pure rotation about the cell corner"
 * to peg a sublattice coset to), so the ideal/CSL argument above doesn't
 * directly apply. This was verified empirically (tests/colourGroups.test.ts),
 * brute-forcing every per-op classifier against the same permutation-
 * consistency property the ideal theory guarantees:
 * - pg: the 2-colouring exists but must run along the axis *perpendicular* to
 *   the glide (class depends on the lattice index that the glide doesn't
 *   shift), not the (n+m) checkerboard.
 * - pmg: same shape, perpendicular to its glide axis.
 * - pgg and p4g: exhaustive search over every per-op classifier found NO
 *   colouring with any translation dependence — only the degenerate
 *   "colour = which point-group op, ignore position" split, which doesn't
 *   produce a sublattice-offset pattern at all. These two are dropped from
 *   colour tiling entirely (single colour only) rather than shipping a
 *   plausible-looking but unverifiable table entry.
 */
import { transformToMatrix } from '@/geometry/affine';
import type { TileTransform } from '@/patterns/types';
import type { WallpaperGroupId } from './groups';

/** Valid colour counts per wallpaper group, ascending, always including 1 (single flat colour). */
export const COLOUR_COUNTS: Record<WallpaperGroupId, number[]> = {
  // Oblique — no rotation of order ≥3, no natural larger count (see module doc).
  p1: [1, 2],
  p2: [1, 2],
  // Rectangular
  pm: [1, 2],
  pg: [1, 2], // 2-colouring runs along the axis the glide doesn't shift (verified empirically)
  pmm: [1, 2],
  pmg: [1, 2], // ditto — perpendicular to its glide axis
  pgg: [1], // no translation-dependent colouring exists (verified — see module doc)
  // Centred rectangular / rhombic
  cm: [1, 2],
  cmm: [1, 2],
  // Square — Gaussian-integer ideals (1+i) [norm 2] and (2) [norm 4], both self-conjugate.
  p4: [1, 2, 4],
  p4m: [1, 2, 4],
  p4g: [1], // non-symmorphic; no translation-dependent colouring exists (verified — see module doc)
  // Hexagonal — Eisenstein-integer ideals. (1-ω) [norm 3] is self-conjugate: all five
  // groups get it. (3+ω) [norm 7] is chiral: rotation-only groups only. The combined
  // index-3 ⊗ index-2 6-colouring needs an order-2 point-group quotient, which p3
  // (order-3 point group) doesn't have.
  p3: [1, 3, 7],
  p31m: [1, 3, 6],
  p3m1: [1, 3, 6],
  p6: [1, 3, 6, 7],
  p6m: [1, 3, 6],
};

export function colourCountsFor(wpgId: WallpaperGroupId): number[] {
  return COLOUR_COUNTS[wpgId] ?? [1];
}

export function isValidColourCount(wpgId: WallpaperGroupId, count: number): boolean {
  return colourCountsFor(wpgId).includes(count);
}

function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

/** Is this placement orientation-reversing (a reflection or glide)? det<0 of
 *  the actual placement matrix, so it's correct however the transform is
 *  represented (struct flips or explicit matrix). */
function isReversing(t: TileTransform): boolean {
  const m = transformToMatrix(t);
  return m[0] * m[3] - m[1] * m[2] < 0;
}

/**
 * Colour class (0..count-1) for one placement, or null when the placement
 * carries no lattice metadata (artist-tile display modes / legacy variants —
 * callers should treat null as "class 0", i.e. render with the main colour).
 */
export function classifyColour(wpgId: WallpaperGroupId, count: number, t: TileTransform): number | null {
  if (count <= 1) return 0;
  if (!t.latticeIndex) return null;
  const [n, m] = t.latticeIndex;
  const opIndex = t.opIndex ?? 0;

  switch (wpgId) {
    case 'p4':
      // Point-group colouring (method 1 above): p4's point group is C4
      // (4 pure rotations, cyclic/abelian) — composing two ops really is
      // index addition mod 4, so opIndex mod count is valid and gives each
      // rotational copy its own colour uniformly, verified by the
      // permutation-consistency test.
      if (count === 2) return mod(opIndex, 2);
      if (count === 4) return mod(opIndex, 4);
      return 0;

    case 'p4m':
      // 2-colour: orientation parity — every mirror flips the colour, so the
      // colouring reads as a proper 2-colouring across the mirror grid (the
      // requested "different colour across mirror lines"). This is a det
      // homomorphism so it's a valid perfect colouring (unlike the naive
      // opIndex-mod scheme the consistency test rejected for D4's mirror-axis
      // ordering). 4-colour stays the translation sublattice (norm-4 ideal).
      if (count === 2) return isReversing(t) ? 1 : 0;
      if (count === 4) return mod(n, 2) * 2 + mod(m, 2);
      return 0;

    // Mirror / glide groups — 2-colour by orientation parity: a reflection or
    // glide flips the colour, so adjacent copies across every mirror/glide line
    // differ (the requested behaviour). Previously these used a translation
    // checkerboard (mod(n+m,2)) which left mirror-image copies within a cell the
    // SAME colour. Orientation parity is the det homomorphism → always a valid
    // perfect colouring (confirmed by the consistency test).
    case 'pm':
    case 'pg':
    case 'cm':
    case 'pmm':
    case 'pmg':
    case 'cmm':
      return count === 2 ? (isReversing(t) ? 1 : 0) : 0;

    case 'p3':
    case 'p31m':
    case 'p3m1':
    case 'p6':
    case 'p6m': {
      // Point-group colouring (method 1): p3's point group has order 3;
      // p31m/p3m1/p6 have order 6; p6m has order 12. 3 and 6 divide all of
      // these (p3 never offers 6 — COLOUR_COUNTS excludes it — so opIndex
      // mod 6 is never evaluated there), so "N-way" tiles (3-way hex, 6-way
      // triangle, …) get one colour per rotational/reflective copy.
      if (count === 3) return mod(opIndex, 3);
      if (count === 6) return mod(opIndex, 6);
      // 7 doesn't divide any of these orders — no point-group analogue
      // exists, so this stays the translation-sublattice colouring (method
      // 2): coset of the norm-7 Eisenstein ideal (3+ω), ω ≡ 4 (mod 7).
      if (count === 7) return mod(n + 5 * m, 7);
      return 0;
    }

    default:
      // p1 (translations only) and p2 (translations + half-turns, no
      // reflections): no orientation-reversing element to hang a colour flip
      // on, so the 2-colouring is the translation checkerboard.
      if (count === 2) return mod(n + m, 2);
      return 0;
  }
}

// ---------------------------------------------------------------------------
// Small hsl→hex helper (no dependency — d3-color is not in the ink/tools hot
// path and this is a handful of lines). Exported for palette.ts's fixed
// 12-colour wheel (brief 10-colours).
// ---------------------------------------------------------------------------

export function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const mm = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (v: number): string =>
    Math.round((v + mm) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Inverse of hslToHex's hue axis — used to reverse-lookup which of the 12
 *  base-palette hues a resolved hex came from (brief 10-colours' colour-
 *  storage migration: hue is never touched between light/dark, so this is a
 *  lossless recovery for any hex this app could have produced). */
export function hexToHue(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  if (delta === 0) return 0;
  let hue: number;
  if (max === r) hue = 60 * (((g - b) / delta) % 6);
  else if (max === g) hue = 60 * ((b - r) / delta + 2);
  else hue = 60 * ((r - g) / delta + 4);
  return hue < 0 ? hue + 360 : hue;
}
